import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppLayout from '../components/layout/AppLayout';
import { getTickets, getTicketStats, getTicketCompanies, getConsultants, getRequesters, bulkUpdateTickets, exportTickets } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import {
  Plus, Search, Filter, AlertTriangle, Clock, Ticket,
  ChevronLeft, ChevronRight, UserX,
  ChevronDown, Check, Server, Tag, Users, User, Layers,
  Activity, CheckCircle, Building2, Download, Calendar,
} from 'lucide-react';

const SERVICE_CATEGORIES = ['Zyra ERP','SAP ERP','Oracle ERP','Custom ERP','Infrastructure','Network','Hardware','Software'];
const STATUS_OPTIONS      = ['open','assigned','in_progress','pending','resolved','closed','reopened'];
const PRIORITY_OPTIONS    = ['critical','high','medium','low'];
const TYPE_OPTIONS        = ['incident','service_request','change_request'];
const MONTH_OPTIONS       = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const CURRENT_YEAR        = new Date().getFullYear();
const YEAR_OPTIONS        = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);
const LIMIT               = 15;

const STATUS_META = {
  open:        { label: 'Open',        color: '#2563eb', bg: '#dbeafe' },
  assigned:    { label: 'Assigned',    color: '#d97706', bg: '#fef3c7' },
  in_progress: { label: 'In Progress', color: 'var(--primary)', bg: 'var(--primary-100)' },
  pending:     { label: 'Pending',     color: '#ea580c', bg: '#ffedd5' },
  resolved:    { label: 'Resolved',    color: '#16a34a', bg: '#dcfce7' },
  closed:      { label: 'Closed',      color: '#6b7280', bg: '#f3f4f6' },
  reopened:    { label: 'Reopened',    color: '#dc2626', bg: '#fee2e2' },
};
const PRIORITY_META = {
  critical: { dot: '#dc2626', label: 'P1', color: '#dc2626' },
  high:     { dot: '#ea580c', label: 'P2', color: '#ea580c' },
  medium:   { dot: '#d97706', label: 'P3', color: '#d97706' },
  low:      { dot: '#16a34a', label: 'P4', color: '#16a34a' },
};
const TIER_COLOR = { L1: '#3b82f6', L2: '#10b981', L3: '#f59e0b', L4: '#ef4444' };

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.open;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: m.bg, color: m.color, whiteSpace: 'nowrap' }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: m.color }} />
      {m.label}
    </span>
  );
}

function PriorityCell({ priority }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.medium;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: m.color }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: m.dot, flexShrink: 0 }} />
      {m.label}
    </span>
  );
}

const Dot = ({ c }) => <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, display: 'inline-block', flexShrink: 0 }} />;

// Icon-trigger + styled dropdown menu (replaces native <select>)
function FancySelect({ value, onChange, options, placeholder, leadingIcon }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = options.find(o => o.value === value);
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 7,
        background: 'var(--bg-input)', color: 'var(--text-main)', fontSize: 13,
        fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box',
        outline: open ? '2px solid var(--primary-light)' : 'none',
      }}>
        {leadingIcon && <span style={{ display: 'flex', color: 'var(--text-muted)', flexShrink: 0 }}>{leadingIcon}</span>}
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? 'var(--text-main)' : 'var(--text-muted)' }}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={15} style={{ flexShrink: 0, color: 'var(--text-muted)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 50,
          background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,0.14)', padding: 6, maxHeight: 320, overflowY: 'auto',
        }}>
          {options.map(o => {
            const active = o.value === value;
            return (
              <button key={o.value || '__all'} type="button"
                onMouseDown={() => { onChange(o.value); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '9px 10px', border: 'none', borderRadius: 8, cursor: 'pointer',
                  background: active ? 'var(--primary-light)' : 'transparent',
                  color: active ? 'var(--primary)' : 'var(--text-main)',
                  fontSize: 13, fontWeight: active ? 600 : 500, fontFamily: 'inherit', textAlign: 'left',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--bg-hover, rgba(0,0,0,0.04))'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ width: 18, display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0, color: 'var(--text-muted)' }}>{o.icon}</span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.label}</span>
                {active && <Check size={15} style={{ flexShrink: 0, color: 'var(--primary)' }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// `lockedStatus` pins the list to one status and hides the status filter —
// used by the Ticket History → Closed Tickets route, which is this same page
// with status fixed to 'closed'.
export default function TicketsPage({ lockedStatus = '' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const qp          = new URLSearchParams(location.search);
  const initProject = qp.get('project_id')   || '';
  const initProjName= qp.get('project_name') || '';
  const initAssignedTo = qp.get('assigned_to') || '';
  const initStatus     = lockedStatus || qp.get('status') || '';

  const [tickets,     setTickets]     = useState([]);
  const [total,       setTotal]       = useState(0);
  const [stats,       setStats]       = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [requesters,  setRequesters]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [page,        setPage]        = useState(1);

  const [selected,    setSelected]    = useState(new Set());
  const [bulkAction,  setBulkAction]  = useState('');
  const [bulkValue,   setBulkValue]   = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  const [projectFilter,     setProjectFilter]     = useState(initProject);
  const [projectFilterName, setProjectFilterName] = useState(initProjName);

  const [filters, setFilters] = useState({
    search: '', status: initStatus, priority: '', ticket_type: '', service_category: '',
    assigned_to: initAssignedTo, raised_by: '', company: '', date: '', month: '', year: '',
  });
  const [quickFilter, setQuickFilter] = useState('');
  // Companies that actually appear on tickets — the filter is hidden entirely
  // until at least one ERP ticket carries a company.
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    getTicketStats().then(r => setStats(r.data)).catch(() => {});
    getTicketCompanies().then(r => setCompanies(r.data || [])).catch(() => {});
    getConsultants().then(r => setConsultants(r.data || [])).catch(() => {});
    getRequesters().then(r => setRequesters(r.data || [])).catch(() => {});
  }, [user]);

  // Shared with the CSV export, which needs the same filters minus paging.
  const buildFilterParams = useCallback(() => {
    const params = {};
    if (filters.search)           params.search           = filters.search;
    if (filters.status)           params.status           = filters.status;
    if (filters.priority)         params.priority         = filters.priority;
    if (filters.ticket_type)      params.ticket_type      = filters.ticket_type;
    if (filters.service_category) params.service_category = filters.service_category;
    if (filters.assigned_to)      params.assigned_to      = filters.assigned_to;
    if (filters.raised_by)        params.raised_by        = filters.raised_by;
    if (filters.company)          params.company          = filters.company;
    if (filters.date)             params.date             = filters.date;
    if (filters.month)            params.month            = filters.month;
    if (filters.year)             params.year             = filters.year;
    if (projectFilter)                  params.project_id   = projectFilter;
    if (quickFilter === 'unassigned')   params.unassigned   = 'true';
    if (quickFilter === 'sla_breached') params.sla_breached = 'true';
    if (quickFilter === 'critical')     params.priority     = 'critical';
    return params;
  }, [filters, quickFilter, projectFilter]);

  const fetchTickets = useCallback(() => {
    setLoading(true);
    getTickets({ ...buildFilterParams(), page, limit: LIMIT })
      .then(res => { setTickets(res.data.tickets || []); setTotal(res.data.total || 0); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [buildFilterParams, page]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await exportTickets(buildFilterParams());
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.ms-excel' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `tickets-${format(new Date(), 'yyyy-MM-dd')}.xls`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to export tickets.');
    } finally {
      setDownloading(false);
    }
  };

  const setFilter = (key, val) => { setFilters(f => ({ ...f, [key]: val })); setPage(1); setSelected(new Set()); };
  const resetAllFilters = () => {
    // Reset never clears a locked status — that's the identity of the page.
    setFilters({ search: '', status: lockedStatus, priority: '', ticket_type: '', service_category: '', assigned_to: '', raised_by: '', company: '', date: '', month: '', year: '' });
    setProjectFilter('');
    setProjectFilterName('');
    setQuickFilter('');
    setPage(1);
    setSelected(new Set());
    // Staying put on a locked-status route; navigating to /tickets would drop
    // the user out of Closed Tickets entirely.
    if (!lockedStatus) navigate('/tickets', { replace: true });
  };
  const setQuick  = (val) => {
    if (val === '') { resetAllFilters(); return; }
    setQuickFilter(v => v === val ? '' : val);
    setPage(1);
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };
  const allSelected = tickets.length > 0 && tickets.every(t => selected.has(t.id));
  const toggleAll   = () => { if (allSelected) setSelected(new Set()); else setSelected(new Set(tickets.map(t => t.id))); };

  const applyBulk = async () => {
    if (!bulkAction || !bulkValue || selected.size === 0) return;
    setBulkLoading(true);
    try {
      const updates = {};
      if (bulkAction === 'status')      updates.status      = bulkValue;
      if (bulkAction === 'priority')    updates.priority    = bulkValue;
      if (bulkAction === 'assigned_to') updates.assigned_to = bulkValue === '__unassign__' ? null : bulkValue;
      await bulkUpdateTickets({ ids: Array.from(selected), updates });
      setSelected(new Set()); setBulkAction(''); setBulkValue('');
      fetchTickets(); getTicketStats().then(r => setStats(r.data)).catch(() => {});
    } catch (err) { alert(err.response?.data?.error || 'Bulk update failed.'); }
    finally { setBulkLoading(false); }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const pageNums   = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const arr = [1];
    if (page > 3) arr.push('…');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) arr.push(i);
    if (page < totalPages - 2) arr.push('…');
    arr.push(totalPages);
    return arr;
  };

  const inputSt = { padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 7, background: 'var(--bg-input)', color: 'var(--text-main)', fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' };

  const clearProjectFilter = () => {
    setProjectFilter('');
    setProjectFilterName('');
    setPage(1);
    navigate('/tickets', { replace: true });
  };

  return (
    <AppLayout pageTitle={
      lockedStatus === 'closed'
        ? 'Closed Tickets'
        : projectFilter
          ? `Tickets — ${projectFilterName || 'Client'}`
          : user?.role === 'user' ? 'My Tickets' : 'All Tickets'
    }>

      {/* ── Project filter banner ── */}
      {projectFilter && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--primary-light)', border: '1px solid var(--primary)', borderRadius: 8, padding: '8px 14px', marginBottom: 16, fontSize: 13 }}>
          <Filter size={14} color="var(--primary)" />
          <span style={{ color: 'var(--primary)', fontWeight: 600 }}>
            Showing tickets for client: <strong>{projectFilterName || projectFilter}</strong>
          </span>
          <button
            onClick={clearProjectFilter}
            style={{ marginLeft: 'auto', padding: '3px 12px', borderRadius: 6, border: '1px solid var(--primary)', background: 'transparent', color: 'var(--primary)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            Clear filter
          </button>
        </div>
      )}

      {/* ── Stats row ── */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 22 }}>
          {[
            { label: 'Open',         value: stats.open_tickets,  color: '#2563eb', key: 'open',        icon: Ticket },
            { label: 'In Progress',  value: stats.in_progress,   color: 'var(--primary)', key: 'in_progress', icon: Activity },
            { label: 'Resolved',     value: stats.resolved,      color: '#16a34a', key: 'resolved',    icon: CheckCircle },
            { label: 'Critical',     value: stats.critical_open, color: '#dc2626', key: 'critical',    icon: AlertTriangle },
            { label: 'SLA Breached', value: stats.sla_breached,  color: '#ea580c', key: 'sla_breach',  icon: Clock },
          ].map(c => (
            <div key={c.label} className="card-3d"
              onClick={() => c.key === 'sla_breach' ? setQuick('sla_breached') : c.key === 'critical' ? setQuick('critical') : setFilter('status', c.key === 'open' ? 'open' : c.key)}
              style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 20, padding: '20px 20px', cursor: 'pointer', boxShadow: '0 6px 18px rgba(15,23,42,0.07)', display: 'flex', alignItems: 'center', gap: 14 }}
            >
              <div style={{ width: 48, height: 48, borderRadius: 15, flexShrink: 0, background: `${c.color}1a`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <c.icon size={23} color={c.color} strokeWidth={2.1} />
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 30, fontWeight: 800, color: 'var(--text-main)', lineHeight: 1 }}>{c.value ?? 0}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginTop: 4 }}>{c.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Filter bar ── */}
      <div className="card" style={{ padding: '14px 16px', marginBottom: 16 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 10 }}>

          {/* Search */}
          <div style={{ position: 'relative', flex: '2 1 220px' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
            <input type="text" style={{ ...inputSt, width: '100%', paddingLeft: 32 }}
              placeholder="Search tickets…"
              value={filters.search} onChange={e => setFilter('search', e.target.value)}
            />
          </div>

          <div style={{ flex: '1 1 150px' }}>
            <FancySelect
              leadingIcon={<Server size={14} />}
              placeholder="All Systems"
              value={filters.service_category}
              onChange={v => setFilter('service_category', v)}
              options={[
                { value: '', label: 'All Systems', icon: <Layers size={14} /> },
                ...SERVICE_CATEGORIES.map(sc => ({ value: sc, label: sc, icon: <Dot c="var(--text-muted)" /> })),
              ]}
            />
          </div>

          {!lockedStatus && (
            <div style={{ flex: '1 1 140px' }}>
              <FancySelect
                leadingIcon={<Filter size={14} />}
                placeholder="All Status"
                value={filters.status}
                onChange={v => setFilter('status', v)}
                options={[
                  { value: '', label: 'All Status', icon: <Layers size={14} /> },
                  ...STATUS_OPTIONS.map(s => ({ value: s, label: (STATUS_META[s]?.label || s.replace(/_/g, ' ')), icon: <Dot c={STATUS_META[s]?.color || 'var(--text-muted)'} /> })),
                ]}
              />
            </div>
          )}

          <div style={{ flex: '1 1 140px' }}>
            <FancySelect
              leadingIcon={<AlertTriangle size={14} />}
              placeholder="All Priority"
              value={filters.priority}
              onChange={v => setFilter('priority', v)}
              options={[
                { value: '', label: 'All Priority', icon: <Layers size={14} /> },
                ...PRIORITY_OPTIONS.map(p => ({ value: p, label: `${PRIORITY_META[p]?.label || ''} · ${p.charAt(0).toUpperCase() + p.slice(1)}`, icon: <Dot c={PRIORITY_META[p]?.dot || 'var(--text-muted)'} /> })),
              ]}
            />
          </div>

          <div style={{ flex: '1 1 140px' }}>
            <FancySelect
              leadingIcon={<Tag size={14} />}
              placeholder="All Types"
              value={filters.ticket_type}
              onChange={v => setFilter('ticket_type', v)}
              options={[
                { value: '', label: 'All Types', icon: <Layers size={14} /> },
                ...TYPE_OPTIONS.map(t => ({ value: t, label: t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), icon: <Tag size={13} /> })),
              ]}
            />
          </div>

          <div style={{ flex: '1 1 150px' }}>
            <FancySelect
              leadingIcon={<Users size={14} />}
              placeholder="All Consultants"
              value={filters.assigned_to}
              onChange={v => setFilter('assigned_to', v)}
              options={[
                { value: '', label: 'All Consultants', icon: <Layers size={14} /> },
                ...consultants.map(c => ({ value: String(c.id), label: c.name, icon: <Users size={13} /> })),
              ]}
            />
          </div>

          {/* Requester of the ticket — the counterpart to "All Consultants"
              (who it's assigned to), matching the Requester column. Shown on
              every login, including a regular user's own My Tickets view. */}
          <div style={{ flex: '1 1 150px' }}>
            <FancySelect
              leadingIcon={<User size={14} />}
              placeholder="All Users"
              value={filters.raised_by}
              onChange={v => setFilter('raised_by', v)}
              options={[
                { value: '', label: 'All Users', icon: <Layers size={14} /> },
                ...requesters.map(u => ({ value: String(u.id), label: u.name, icon: <User size={13} /> })),
              ]}
            />
          </div>

          {/* Company the ticket belongs to (its own Zyra ERP tenant, or its
              linked client Project). Only rendered once some ticket actually
              carries one. */}
          {companies.length > 0 && (
            <div style={{ flex: '1 1 160px' }}>
              <FancySelect
                leadingIcon={<Building2 size={14} />}
                placeholder="All Companies"
                value={filters.company}
                onChange={v => setFilter('company', v)}
                options={[
                  { value: '', label: 'All Companies', icon: <Layers size={14} /> },
                  ...companies.map(c => ({
                    value: c.company_name,
                    label: `${c.company_name} (${c.ticket_count})`,
                    icon: <Building2 size={13} />,
                  })),
                ]}
              />
            </div>
          )}

          {/* Creation date/month/year — each independent, shown on every login. */}
          <div style={{ position: 'relative', flex: '1 1 140px' }}>
            <Calendar size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
            <input type="date" title="Exact created date" style={{ ...inputSt, width: '100%', paddingLeft: 32 }}
              value={filters.date}
              onChange={e => setFilter('date', e.target.value)}
            />
          </div>
          <div style={{ flex: '1 1 130px' }}>
            <FancySelect
              leadingIcon={<Calendar size={14} />}
              placeholder="Month"
              value={filters.month}
              onChange={v => setFilter('month', v)}
              options={[
                { value: '', label: 'Month', icon: <Layers size={14} /> },
                ...MONTH_OPTIONS.map((m, i) => ({ value: String(i + 1), label: m, icon: <Calendar size={13} /> })),
              ]}
            />
          </div>
          <div style={{ flex: '1 1 110px' }}>
            <FancySelect
              leadingIcon={<Calendar size={14} />}
              placeholder="Year"
              value={filters.year}
              onChange={v => setFilter('year', v)}
              options={[
                { value: '', label: 'Year', icon: <Layers size={14} /> },
                ...YEAR_OPTIONS.map(y => ({ value: String(y), label: String(y), icon: <Calendar size={13} /> })),
              ]}
            />
          </div>
        </div>

        {/* Quick filters + actions. Hidden on a locked-status route: SLA
            Breached and Unassigned can't match closed tickets, so they'd only
            offer dead ends. */}
        <div style={{ display: lockedStatus ? 'none' : 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Filter:</span>
          {[
            { key: '',              label: 'All',         icon: null },
            { key: 'unassigned',    label: 'Unassigned',  icon: <UserX size={11} /> },
            { key: 'sla_breached',  label: 'SLA Breached',icon: <AlertTriangle size={11} /> },
            { key: 'critical',      label: 'Critical',    icon: <AlertTriangle size={11} /> },
          ].map(q => {
            const isActive = q.key === '' ? quickFilter === '' : quickFilter === q.key;
            return (
              <button key={q.key} onClick={() => setQuick(q.key)} style={{
                padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: `1.5px solid ${isActive ? 'var(--primary)' : 'var(--border)'}`,
                background: isActive ? 'var(--primary-light)' : 'transparent',
                color: isActive ? 'var(--primary)' : 'var(--text-muted)',
                display: 'flex', alignItems: 'center', gap: 5, transition: 'all 0.15s',
              }}>
                {q.icon}{q.label}
              </button>
            );
          })}
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 500 }}>
            {loading ? '…' : `${total} ticket${total !== 1 ? 's' : ''}`}
          </span>
          <button className="btn btn-outline btn-sm" onClick={handleDownload} disabled={downloading} style={{ gap: 5 }}>
            <Download size={14} /> {downloading ? 'Exporting…' : 'Download'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => navigate('/tickets/new')} style={{ gap: 5 }}>
            <Plus size={14} /> New Ticket
          </button>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selected.size > 0 && user?.role !== 'user' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '10px 16px', borderRadius: 10, marginBottom: 12,
          background: 'var(--primary-light)', border: '1.5px solid #ddd6fe',
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)' }}>
            {selected.size} selected
          </span>
          <div style={{ width: 1, height: 18, background: 'var(--primary-200)' }} />
          <select style={{ ...inputSt, padding: '6px 10px', fontSize: 12 }} value={bulkAction} onChange={e => { setBulkAction(e.target.value); setBulkValue(''); }}>
            <option value="">Bulk Action…</option>
            <option value="status">Change Status</option>
            <option value="priority">Change Priority</option>
            {user?.role === 'admin' && <option value="assigned_to">Assign Consultant</option>}
          </select>
          {bulkAction === 'status' && (
            <select style={{ ...inputSt, padding: '6px 10px', fontSize: 12 }} value={bulkValue} onChange={e => setBulkValue(e.target.value)}>
              <option value="">Select Status…</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g,' ')}</option>)}
            </select>
          )}
          {bulkAction === 'priority' && (
            <select style={{ ...inputSt, padding: '6px 10px', fontSize: 12 }} value={bulkValue} onChange={e => setBulkValue(e.target.value)}>
              <option value="">Select Priority…</option>
              {PRIORITY_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
          {bulkAction === 'assigned_to' && (
            <select style={{ ...inputSt, padding: '6px 10px', fontSize: 12 }} value={bulkValue} onChange={e => setBulkValue(e.target.value)}>
              <option value="">Select Consultant…</option>
              <option value="__unassign__">— Unassign —</option>
              {consultants.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <button className="btn btn-primary btn-sm" onClick={applyBulk} disabled={!bulkValue || bulkLoading}>
            {bulkLoading ? 'Applying…' : 'Apply'}
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {/* ── Table ── */}
      <div className="table-wrap">
        {loading ? (
          <div className="flex-center" style={{ padding: 64 }}>
            <div className="spinner" style={{ width: 32, height: 32 }} />
          </div>
        ) : tickets.length === 0 ? (
          <div className="empty-state">
            <Ticket size={40} color="var(--gray-300)" style={{ marginBottom: 12 }} />
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>No tickets found</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>Try adjusting your filters</div>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/tickets/new')} style={{ gap: 5 }}>
              <Plus size={14} /> Create Ticket
            </button>
          </div>
        ) : (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  {user?.role !== 'user' && (
                    <th style={{ width: 44, padding: '12px 8px 12px 16px' }}>
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ cursor: 'pointer', accentColor: 'var(--primary)', width: 14, height: 14 }} />
                    </th>
                  )}
                  <th style={{ width: 120 }}>Ticket #</th>
                  <th>Subject</th>
                  {user?.role !== 'user' && <th style={{ width: 130 }}>Requester</th>}
                  <th style={{ width: 100 }}>Priority</th>
                  <th style={{ width: 120 }}>Status</th>
                  <th style={{ width: 140 }}>Assigned To</th>
                  <th style={{ width: 100 }}>SLA</th>
                  <th style={{ width: 100 }}>Created</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map(t => {
                  const slaDue = t.effective_sla_due_at || t.sla_due_at;
                  const isBreached = slaDue && new Date(slaDue) < new Date() && !['resolved','closed'].includes(t.status);
                  const tierColor  = TIER_COLOR[t.support_tier];
                  return (
                    <tr key={t.id} onClick={() => navigate(`/tickets/${t.id}`)}>
                      {user?.role !== 'user' && (
                        <td style={{ padding: '12px 8px 12px 16px' }} onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={selected.has(t.id)}
                            onChange={e => toggleSelect(t.id, e)}
                            style={{ cursor: 'pointer', accentColor: 'var(--primary)', width: 14, height: 14 }}
                          />
                        </td>
                      )}

                      {/* Ticket number */}
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, fontWeight: 700, color: 'var(--primary)', background: 'var(--primary-light)', padding: '2px 7px', borderRadius: 5 }}>
                            {t.ticket_number}
                          </span>
                        </div>
                        {t.support_tier && tierColor && (
                          <span style={{ fontSize: 10, fontWeight: 700, color: tierColor, background: tierColor + '18', padding: '1px 6px', borderRadius: 4, marginTop: 3, display: 'inline-block' }}>
                            {t.support_tier}
                          </span>
                        )}
                      </td>

                      {/* Title */}
                      <td>
                        <div style={{ fontWeight: 500, color: 'var(--text-main)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.is_from_zyra && <span style={{ marginRight: 5, fontSize: 11 }}>⚡</span>}
                          {t.escalated    && <span style={{ marginRight: 5, fontSize: 11 }}>🔺</span>}
                          {t.title}
                        </div>
                        {(t.service_category || t.category) && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                            {[t.service_category, t.category].filter(Boolean).join(' › ')}
                          </div>
                        )}
                      </td>

                      {/* Requester */}
                      {user?.role !== 'user' && (
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {t.raised_by_name || '—'}
                        </td>
                      )}

                      {/* Priority */}
                      <td><PriorityCell priority={t.priority} /></td>

                      {/* Status */}
                      <td><StatusBadge status={t.status} /></td>

                      {/* Assigned */}
                      <td style={{ fontSize: 12 }}>
                        {t.assigned_to_name ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                              {t.assigned_to_name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                            </div>
                            <span style={{ color: 'var(--text-sub)', maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {t.assigned_to_name}
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: '#d97706', fontStyle: 'italic', fontSize: 11 }}>Unassigned</span>
                        )}
                      </td>

                      {/* SLA */}
                      <td>
                        {slaDue ? (
                          <span style={{ fontSize: 11, fontWeight: 600, color: isBreached ? '#dc2626' : '#16a34a', display: 'flex', alignItems: 'center', gap: 4 }}>
                            {isBreached ? <AlertTriangle size={11} /> : <Clock size={11} />}
                            {format(new Date(slaDue), 'MMM d HH:mm')}
                          </span>
                        ) : <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>}
                      </td>

                      {/* Created */}
                      <td style={{ fontSize: 11.5, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        {format(new Date(t.created_at), 'MMM d, yyyy')}
                        <div style={{ fontSize: 10.5 }}>{format(new Date(t.created_at), 'HH:mm')}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 20px', borderTop: '1px solid var(--border)',
                background: 'var(--bg-table-head)',
              }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Page {page} of {totalPages} · {total} tickets
                </span>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <ChevronLeft size={14} color="var(--text-muted)" />
                  </button>
                  {pageNums().map((n, i) => (
                    <button key={i} onClick={() => typeof n === 'number' && setPage(n)}
                      style={{
                        width: 32, height: 32, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: typeof n === 'number' ? 'pointer' : 'default',
                        border: `1px solid ${n === page ? 'var(--primary)' : 'var(--border)'}`,
                        background: n === page ? 'var(--primary)' : 'var(--bg-card)',
                        color: n === page ? '#fff' : typeof n === 'number' ? 'var(--text-main)' : 'var(--text-muted)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >{n}</button>
                  ))}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: page === totalPages ? 'not-allowed' : 'pointer', opacity: page === totalPages ? 0.4 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <ChevronRight size={14} color="var(--text-muted)" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
